/* ==========================================================================
   ASCEND — product page
   Size chips · volume tiers · quantity stepper · subscribe & save ·
   sticky add-to-cart · certificate-of-analysis card

   The reference site is a React app holding selected variant and quantity in
   state. Shopify posts a variant id to /cart/add, so the chips write into a
   hidden input and everything price-shaped re-renders from a variant map that
   Liquid embedded as JSON.

   ── SUBSCRIBE PRICING IS SHOPIFY'S NUMBER, NOT OURS ──
   The subscribe option never computes "20% off" itself. It reads the price out
   of the variant's selling_plan_allocation, which is the figure Shopify will
   actually charge for that plan. If a variant has no allocation, the subscribe
   option is not offered for it at all rather than falling back to a guess.

   ── VOLUME TIERS ARE A DISPLAY OF THE STORE'S AUTOMATIC DISCOUNTS ──
   The Buy 1 / 3 / 5 / 10+ cards show the per-vial price and line total that
   the store's automatic discounts (Volume 3+ / 5+ / 10+) will produce at
   checkout. The percentages come from the section settings and MUST match
   those discounts — the page displays a saving, Shopify applies it. Tiers
   apply to one-time purchases only; in subscribe mode the cards show the plan
   price and a note says so.

   ── THE COA CARD FOLLOWS THE SELECTED SIZE ──
   Each lot's figures (lot, report number, purity, net content, issue date,
   certificate URL) are embedded by Liquid from ascend.coa_docs. Selecting a
   size swaps the card and re-points both "View COA" links to that lot's own
   certificate. A size with no published lot falls back to the product's first
   lot rather than showing a stale mismatch silently — the size label in the
   card sub-line makes the substitution visible.
   ========================================================================== */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-pdp]');
    if (!root) return;
    if (root.dataset.pdpBound === '1') return;
    root.dataset.pdpBound = '1';

    var dataEl = root.querySelector('[data-pdp-variants]');
    if (!dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var coaDocs = [];
    var coaEl = root.querySelector('[data-pdp-coa]');
    if (coaEl) { try { coaDocs = JSON.parse(coaEl.textContent) || []; } catch (e) { coaDocs = []; } }

    var MONEY = root.getAttribute('data-money-format') || '${{amount}}';
    /* 'vial' for compounds, 'kit' for Spray Kits — read off the section so the
       per-unit label on the tier cards never says 'vial' for a kit. */
    var UNIT = root.getAttribute('data-pdp-unit') || 'vial';

    /* Query ALL matches, not the first: the sticky bar carries a second copy of
       the chips, the stepper and the total, and both copies must stay in step. */
    var idInputs  = root.querySelectorAll('[data-pdp-variant-id]');
    var chips     = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-chip]'));
    var priceEls  = root.querySelectorAll('[data-pdp-price]');
    var comparEls = root.querySelectorAll('[data-pdp-compare]');
    var sizeEls   = root.querySelectorAll('[data-pdp-selected-size]');
    var qtyInputs = root.querySelectorAll('[data-pdp-qty]');
    var totalEls  = root.querySelectorAll('[data-pdp-total]');
    var addBtns   = root.querySelectorAll('[data-pdp-add]');
    var soldOut   = root.querySelector('[data-pdp-soldout]');
    var planInput = root.querySelector('[data-pdp-selling-plan]');
    var modeToggle = root.querySelector('[data-pdp-mode-toggle]');
    var modeFlag  = root.querySelector('[data-pdp-mode-flag]');
    var modeNote  = root.querySelector('[data-pdp-mode-note]');
    var tiers     = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-tier]'));
    var tiersNote = root.querySelector('[data-pdp-tiers-note]');
    var coaLinks  = root.querySelectorAll('[data-pdp-coa-link]');

    /* 'subscribe' or 'onetime'. The default comes from a theme setting; it is
       forced back to one-time below if the product has no selling plan, so the
       page can never sit in a subscribe state it cannot actually submit. The
       checkbox is the source of truth once it exists — reading it keeps a
       browser-restored state (back button) from disagreeing with what is ticked. */
    var mode = root.getAttribute('data-default-mode') === 'subscribe' ? 'subscribe' : 'onetime';
    if (modeToggle) mode = modeToggle.checked ? 'subscribe' : 'onetime';

    var sheet     = root.querySelector('[data-pdp-size-sheet]');
    var sheetOpts = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-sheet-opt]'));
    var stickyForm = root.querySelector('.pdp-sticky-form');

    /* Has the shopper actually PICKED a size, or is this just Liquid's
       preselection? With one variant there is nothing to pick, so it counts as
       chosen; otherwise only a real interaction counts. (?variant= in the URL
       is NOT a signal — Shopify adds it server-side on every page view.) */
    var multi = variants.length > 1;
    var sizeChosen = !multi;

    var current = variants.filter(function (v) {
      return String(v.id) === String(idInputs[0] && idInputs[0].value);
    })[0] || variants[0];
    var quantity = 1;

    /* Shopify's money_format is the source of truth for currency rendering.
       money_without_trailing_zeros behaviour: drop the decimals only when the
       amount is whole. */
    function fmt(cents) {
      var n = Math.round(cents) / 100;
      var parts = n.toFixed(2).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var body = parts[1] === '00' ? whole : whole + '.' + parts[1];
      return MONEY.replace(/\{\{\s*amount[^}]*\}\}/, body);
    }

    /* The plan offered for the CURRENT variant, or null. */
    function planFor(variant) {
      var plans = (variant && variant.selling_plans) || [];
      return plans.length ? plans[0] : null;
    }

    /* Tier definitions read off the cards themselves, sorted ascending, so the
       markup is the single source of truth for both display and bracket math. */
    var tierDefs = tiers.map(function (t) {
      return { qty: parseInt(t.getAttribute('data-qty'), 10) || 1,
               pct: parseFloat(t.getAttribute('data-pct')) || 0, el: t };
    }).sort(function (a, b) { return a.qty - b.qty; });

    /* The discount bracket a quantity falls in: the highest tier whose minimum
       the count meets. Volume pricing STACKS on the subscription price — the
       store's automatic discounts apply on subscription lines too — so the
       bracket is taken off whichever per-vial price the mode produces. */
    function pctFor(q) {
      var pct = 0;
      tierDefs.forEach(function (t) { if (q >= t.qty) pct = t.pct; });
      return pct;
    }

    /* Base per-vial price under the active mode. Subscribe falls back to the
       one-time price when this variant carries no allocation. */
    function basePrice() {
      var plan = planFor(current);
      return (mode === 'subscribe' && plan) ? plan.price : current.price;
    }

    /* Per-vial price after the bracket discount. Rounded to the cent per unit,
       which is how Shopify allocates a percentage discount across a line. */
    function unitPrice(q) {
      return Math.round(basePrice() * (100 - pctFor(q)) / 100);
    }

    function lineTotal(q) { return unitPrice(q) * q; }

    function setQty(q) {
      q = Math.max(1, Math.min(999, q || 1));
      quantity = q;
      render();
    }

    /* ── COA card ── */
    function docFor(variant) {
      if (!coaDocs.length) return null;
      var d = coaDocs.filter(function (x) { return String(x.variant_id) === String(variant.id); })[0];
      return d || coaDocs[0];
    }
    function setText(sel, txt) {
      var el = root.querySelector(sel);
      if (el) el.textContent = txt;
    }
    function renderCoa() {
      var d = docFor(current);
      if (!d) return;
      coaLinks.forEach(function (a) { a.href = d.url; });
      setText('[data-coa-size]', d.size);
      setText('[data-coa-lot]', d.lot);
      setText('[data-coa-cert]', d.cert);
      if (d.purity && d.purity !== 'N/A') setText('[data-coa-purity]', d.purity + '%');
      if (d.net && d.net !== 'N/A') setText('[data-coa-net]', d.net + ' mg');
      setText('[data-coa-issued]', d.issued);
    }

    function render() {
      var plan = planFor(current);
      /* A subscribe state is only sustainable while a plan exists for this
         variant — switching to a size with no plan silently returns to
         one-time rather than leaving an unsubmittable selection on screen. */
      if (mode === 'subscribe' && !plan) mode = 'onetime';

      var showingPlan = mode === 'subscribe' && plan;
      priceEls.forEach(function (el) {
        el.textContent = showingPlan ? plan.price_formatted : current.price_formatted;
      });
      comparEls.forEach(function (el) {
        /* Struck price is the one-time price when a plan is active, otherwise
           the variant's own compare-at. Both are "what you would have paid". */
        if (showingPlan) {
          el.textContent = current.price_formatted; el.hidden = false;
        } else if (current.compare_at_price && current.compare_at_price > current.price) {
          el.textContent = current.compare_at_price_formatted; el.hidden = false;
        } else { el.hidden = true; }
      });
      if (planInput) planInput.value = showingPlan ? plan.id : '';
      sizeEls.forEach(function (el) { el.textContent = current.title; });
      idInputs.forEach(function (el) { el.value = current.id; });
      qtyInputs.forEach(function (el) { if (el.value !== String(quantity)) el.value = quantity; });

      var total = lineTotal(quantity);
      totalEls.forEach(function (el) { el.textContent = fmt(total); });

      /* ── Tier cards ──
         Every card is re-priced for the current size and mode: per-vial price,
         line total, and the struck full price when a bracket discount applies.
         The lit card is the bracket the CURRENT quantity falls in, so typing 7
         in the stepper lights "Buy 5" — the card is a statement of the price
         being charged, not just a button that was tapped. */
      var activeDef = null;
      tierDefs.forEach(function (t) { if (quantity >= t.qty) activeDef = t; });
      var base = basePrice();
      tierDefs.forEach(function (t) {
        var pct = t.pct;
        var unit = Math.round(base * (100 - pct) / 100);
        var unitEl = t.el.querySelector('[data-tier-unit]');
        var totEl  = t.el.querySelector('[data-tier-total]');
        var wasEl  = t.el.querySelector('[data-tier-was]');
        if (unitEl) {
          unitEl.textContent = (pct > 0 || showingPlan) ? fmt(unit) + ' / ' + UNIT : 'Standard';
        }
        if (totEl) totEl.textContent = fmt(unit * t.qty);
        if (wasEl) {
          var full = current.price * t.qty;
          var showWas = (unit * t.qty) < full;
          wasEl.textContent = fmt(full);
          wasEl.hidden = !showWas;
        }
        var on = activeDef === t;
        t.el.classList.toggle('is-selected', on);
        t.el.setAttribute('aria-checked', String(on));
      });
      if (tiersNote) tiersNote.hidden = false;

      sheetOpts.forEach(function (b) {
        b.classList.toggle('is-selected',
          String(b.getAttribute('data-pdp-sheet-opt')) === String(current.id));
      });

      chips.forEach(function (c) {
        var on = String(c.getAttribute('data-pdp-chip')) === String(current.id);
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', String(on));
        c.tabIndex = on ? 0 : -1;
      });

      if (modeToggle) {
        modeToggle.checked = !!showingPlan;
        /* Disabled rather than hidden when this size carries no plan: hiding it
           reflows the whole purchase column every time a size chip is tapped. */
        modeToggle.disabled = !plan;
        var row = modeToggle.closest('[data-pdp-sub-row]');
        if (row) {
          row.classList.toggle('is-on', !!showingPlan);
          row.classList.toggle('is-disabled', !plan);
        }
      }

      /* Saving figure, derived from the two real prices rather than a hardcoded
         percentage, so it always matches whatever the plan is set to. Shows
         MONEY, not the percentage — the headline already states the percentage. */
      if (plan) {
        var off = current.price - plan.price;
        if (modeFlag) {
          modeFlag.textContent = 'Save ' + fmt(off);
          modeFlag.hidden = off <= 0;
        }
        if (modeNote) { modeNote.textContent = plan.name || ''; modeNote.hidden = !plan.name; }
      } else {
        if (modeFlag) modeFlag.hidden = true;
        if (modeNote) modeNote.hidden = true;
      }

      var available = current.available;
      addBtns.forEach(function (b) {
        b.disabled = !available;
        var l = b.querySelector('[data-pdp-add-label]');
        if (l) l.textContent = available ? b.getAttribute('data-label-add') : b.getAttribute('data-label-soldout');
      });
      if (soldOut) soldOut.hidden = available;

      renderCoa();

      /* Mirror the chosen size into the URL so a refresh or a shared link
         lands on the same variant. Only after a deliberate size choice, and
         never inside the theme editor: the editor tracks the preview frame's
         URL, and rewriting it on every render fights the editor's own
         navigation and section re-rendering. */
      if (sizeChosen && !(window.Shopify && window.Shopify.designMode) && window.history && history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', current.id);
        history.replaceState({}, '', u.toString());
      }
    }

    function select(id, explicit) {
      var f = variants.filter(function (v) { return String(v.id) === String(id); })[0];
      if (!f) return;
      current = f;
      /* Any deliberate size interaction satisfies the sticky-bar prompt. */
      if (explicit !== false) sizeChosen = true;
      render();
    }

    /* ── Size sheet ── (the sticky bar's chips are hidden on phones) */
    function openSheet() {
      if (!sheet) return;
      sheet.hidden = false;
      document.body.style.overflow = 'hidden';
      var pick = sheetOpts.filter(function (b) { return !b.disabled; })[0];
      if (pick) pick.focus();
    }
    function closeSheet() {
      if (!sheet) return;
      sheet.hidden = true;
      document.body.style.overflow = '';
    }

    chips.forEach(function (c, i) {
      c.addEventListener('click', function () { select(c.getAttribute('data-pdp-chip')); });
      c.addEventListener('keydown', function (e) {
        var d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
              : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = chips[(i + d + chips.length) % chips.length];
        n.focus(); select(n.getAttribute('data-pdp-chip'));
      });
    });

    /* Tier cards set the quantity outright. Arrow keys move between cards. */
    tierDefs.forEach(function (t, i) {
      t.el.addEventListener('click', function () { setQty(t.qty); });
      t.el.addEventListener('keydown', function (e) {
        var d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
              : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = tierDefs[(i + d + tierDefs.length) % tierDefs.length];
        n.el.focus(); setQty(n.qty);
      });
    });

    if (sheet) {
      root.querySelectorAll('[data-pdp-sheet-close]').forEach(function (el) {
        el.addEventListener('click', closeSheet);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sheet.hidden) closeSheet();
      });
      sheetOpts.forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.disabled) return;
          select(b.getAttribute('data-pdp-sheet-opt'));
          closeSheet();
          /* Complete the action they started: they tapped Add to cart and were
             interrupted for one missing detail. */
          if (stickyForm) {
            if (stickyForm.requestSubmit) stickyForm.requestSubmit();
            else stickyForm.submit();
          }
        });
      });
    }

    /* Intercept only the STICKY form — the in-page button sits under visible
       size chips, so a preselection there is a fair default. */
    if (stickyForm) {
      stickyForm.addEventListener('submit', function (e) {
        if (multi && !sizeChosen && sheet) {
          e.preventDefault();
          openSheet();
        }
      });
    }

    if (modeToggle) {
      modeToggle.addEventListener('change', function () {
        mode = modeToggle.checked ? 'subscribe' : 'onetime';
        render();
      });
    }

    root.querySelectorAll('[data-pdp-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setQty(quantity + (parseInt(btn.getAttribute('data-pdp-step'), 10) || 0));
      });
    });
    qtyInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        input.value = input.value.replace(/\D/g, '');
        var n = parseInt(input.value, 10);
        quantity = isFinite(n) && n > 0 ? Math.min(n, 999) : 1;
        render();
      });
      input.addEventListener('blur', function () { setQty(quantity); });
      input.addEventListener('focus', function () { input.select(); });
    });

    /* ── Sticky add-to-cart ── shown once the in-page button leaves the viewport. */
    var sticky = root.querySelector('[data-pdp-sticky]');
    var anchor = root.querySelector('[data-pdp-add-anchor]');
    if (sticky && anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(anchor);
    }

    /* ── Image zoom ── */
    var zoom = root.querySelector('[data-pdp-zoom]');
    var opener = root.querySelector('[data-pdp-zoom-open]');
    if (zoom && opener) {
      var setZoom = function (on) {
        zoom.classList.toggle('is-open', on);
        zoom.setAttribute('aria-hidden', String(!on));
        document.body.classList.toggle('no-scroll', on);
      };
      opener.addEventListener('click', function () { setZoom(true); });
      zoom.addEventListener('click', function () { setZoom(false); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && zoom.classList.contains('is-open')) setZoom(false);
      });
    }

    render();
  }

  function boot() {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
    document.addEventListener('shopify:section:load', init);
  }
  boot();
})();
